/**
 * @fileoverview Main application component with routing and state management
 * @module MainApp
 *
 * DEPENDENCIES:
 * - react-router-dom
 * - ./lib/logger
 * - ./lib/apiClient
 */

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from './config/supabase';
import { ThemeContext } from './contexts/ThemeContext';
import apiClient from './lib/apiClient';
import logger from './lib/logger';
import TopNav from './components/TopNav/TopNav';
import BackendUnreachableBanner from './components/BackendUnreachableBanner';
import ScraperAuthBanner from './components/ScraperAuthBanner/ScraperAuthBanner';
import NavSidebar from './components/NavSidebar/NavSidebar';
import Home from './components/Home/Home';
import Dashboard from './components/Dashboard/Dashboard';
import ConnectionTypes from './components/ConnectionTypes/ConnectionTypes';
import Products from './components/Products/Products';
import Capabilities from './components/Capabilities/Capabilities';
import SolicitationList from './components/Solicitations/SolicitationList';     // H.18.1 Phase 7.4.D
import SolicitationDetail from './components/Solicitations/SolicitationDetail'; // H.18.1 Phase 7.4.D
import Settings from './components/Settings/Settings';
import GenAIDashboard from './components/GenAIDashboard/GenAIDashboard';
import TechStackAnalysis from './components/TechStackAnalysis';
import ZeroTrust from './components/ZeroTrust';
import ScraperDashboard from './components/ScraperDashboard/ScraperDashboard';
import MemoryDashboard from './components/MemoryDashboard/MemoryDashboard';
import NotFound from './components/NotFound/NotFound';
import SessionExpired from './components/SessionExpired/SessionExpired';
import Breadcrumbs from './components/Breadcrumbs/Breadcrumbs';
import DataGapAnalysis from './components/DataGapAnalysis';
import VendorCompetitiveAnalysis from './components/VendorCompetitiveAnalysis/VendorCompetitiveAnalysis';
import VendorsPage from './components/Vendors/VendorsPage';
import AccountListsPage from './components/AccountLists/AccountListsPage';
import QuotesPage from './components/QuotesPage/QuotesPage';
import PaloWelcome from './components/PaloWelcome';
import FlagReview from './components/FlagReview';
import RuleCandidates from './components/RuleCandidates';
import ClassifierHealth from './components/ClassifierHealth';
import Triage from './components/Triage';
import BacklogBuster from './components/BacklogBuster';
import { useRole } from './hooks/useRole';
import useConnectorHealthAlerts from './hooks/useConnectorHealthAlerts';
import { usePinnedDepartments } from './hooks/useUserPreferences';
import { useAccountLists } from './hooks/useAccountLists';
import { ListsProvider } from './contexts/ListsContext';
import { useTimeFilter } from './hooks/useTimeFilter';
import { TimeFilterProvider } from './contexts/TimeFilterContext';
import { useFiscalBasis } from './hooks/useFiscalBasis';
import { FiscalBasisProvider } from './contexts/FiscalBasisContext';
import ChatWidget from './components/ChatWidget/ChatWidget';
import ClassificationAgentPanel from './components/ClassificationAgent';
import { ClassificationAgentProvider } from './contexts/ClassificationAgentContext';
import SalesAICanvas from './components/SalesAICanvas';
import AccountWorkspace from './components/AccountWorkspace/AccountWorkspace';
import AccountsPage from './components/Accounts/AccountsPage';
import DailyDigest from './components/DailyDigest/DailyDigest';
import NewsFeed from './components/NewsFeed/NewsFeed';
import OpportunityDetail from './components/Opportunities/OpportunityDetail';
import OpportunitiesPage from './components/Opportunities/OpportunitiesPage';
import ContactsPage from './components/Contacts/ContactsPage';
import RecordingsPage from './components/RecordingsPage';
import TasksPage from './components/TaskCanvas/TasksPage';
import QuickCapture from './components/QuickCapture/QuickCapture';
import { CommandCenterPage } from './components/CommandCenter';
import MobileTabBar from './components/MobileTabBar/MobileTabBar';
import useDocumentTitle from './hooks/useDocumentTitle';
import { useAuth } from './contexts/AuthContext';
import { capabilities } from './platform';
import { SECTION_ALIASES, LEGACY_SECTION_ROUTES, DEFAULT_SECTION } from './components/Settings/Settings';
import './App.css';
// Deprecated Tailwind-ish utilities still used by the data-tool pages that
// moved out of Settings (Procurement, Departments) — see the file header.
import './styles/legacy-utilities.css';

// Lazy-loaded — the legacy ClassificationUnified is 4000+ lines and pulls
// in recharts/dnd-kit. Eager-importing it forced the entire bundle to
// parse + execute on every page load, which was contributing to the
// /classification RESULT_CODE_HUNG crash. Now it's a separate chunk
// that loads only when /classification is navigated to.
const ClassificationUnified = React.lazy(() => import('./components/ClassificationIntelligence/ClassificationUnified'));

// Data workbenches evicted from Settings (2026-08 redesign): they are app
// destinations now, code-split so their weight stays out of the shell.
const MainClassificationPage = React.lazy(() => import('./components/MainClassificationPage/MainClassificationPage'));
const IntegratedProcurementDashboard = React.lazy(() => import('./components/IntegratedProcurementDashboard/IntegratedProcurementDashboard'));
const DepartmentManager = React.lazy(() => import('./components/DepartmentManager/DepartmentManager'));
const DepartmentBrandLibrary = React.lazy(() => import('./components/DepartmentBrandLibrary/DepartmentBrandLibrary'));
const ZTCategoryMapping = React.lazy(() => import('./components/ZTCategoryMapping/ZTCategoryMapping'));
const ZTMappingEval = React.lazy(() => import('./components/ZTMappingEval/ZTMappingEval'));

// Heavy visualization routes are code-split so three.js / R3F / gsap / d3
// stay out of the initial bundle (they were multi-MB of parse+execute on
// every cold start, which matters double in the iOS WKWebView shell).
const TechWorld = React.lazy(() => import('./components/TechWorld'));
const TechWorldLab = React.lazy(() => import('./components/TechWorld/Lab/Lab'));
const AtlasRoot = React.lazy(() => import('./components/Atlas/AtlasRoot'));
const SankeyFlowChart = React.lazy(() => import('./components/SankeyFlowChart/SankeyFlowChart'));

// 3D routes never mount in the native iOS shell — WKWebView memory ceilings
// can kill the page on older devices (roadmap A4). On native they bounce to
// /home; on web they load their chunk on first navigation.
const ThreeDRoute = ({ children }) =>
  capabilities.threeD ? (
    <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
      {children}
    </React.Suspense>
  ) : (
    <Navigate to="/home" replace />
  );

const PAGE_TITLES = {
  'welcome': 'Welcome',
  'home': 'Home',
  'digest': 'Daily Digest',
  'dashboard': 'Dashboard',
  'tech-stack': 'Tech Stack',
  'sankey': 'Spending Flow',
  'classification/overview': 'Classification · Overview',
  'classification/health':   'Classification · Health',
  'classification/triage':   'Classification · Triage',
  'classification/flags':    'Classification · Flags',
  'classification/rules':    'Classification · Rule Candidates',
  'zero-trust': 'Zero Trust',
  'stack-map': 'Stack Map',
  'stack-map/lab': 'Stack Map Lab',
  'atlas': 'Atlas',
  'scraper': 'Scraper Ops',
  'memory': 'Memory Dashboard',
  'genai': 'AI Analytics',
  'data': 'Data',
  'backlog-buster': 'Backlog Buster',
  'settings': 'Settings',
  'classification-hub': 'Classification Hub',
  'procurement': 'Procurement Dashboard',
  'departments': 'Department Management',
  'zt-mapping': 'ZT Category Mapping',
  'zt-mapping-eval': 'ZT Mapping Evaluation',
  'vendor-analysis': 'Vendor Competitive Analysis',
  'vendors': 'Vendors',
  'sales-ai': 'Sales AI Canvas',
  'workspace': 'Account Workspace',
  'accounts': 'Accounts',
  'contacts': 'Contacts',
  'opportunities': 'Opportunities',
  'recordings': 'My Recordings',
  'command-center': 'Command Center',
};

// Atlas sub-route labels for breadcrumbs
const ATLAS_ROUTES = {
  'home': 'Security Map',
  'frameworks': 'Frameworks',
  'products': 'Products',
  'personas': 'Personas',
  'scenarios': 'Scenarios',
  'glossary': 'Glossary',
  'diagrams': 'Diagrams',
};

// Routes that show California state-wide aggregates rather than data
// scoped to a single department. On these pages the dept selector and
// favorite chips in TopNav are suppressed (they imply a filter that
// doesn't apply) and the breadcrumb skips the department crumb.
const STATEWIDE_ROUTES = new Set(['vendor-analysis', 'vendors']);

// Routes that use account list focus instead of department focus
const ACCOUNT_LIST_ROUTES = new Set(['sales-ai']);

// Routes that own their ENTIRE filter surface — the TODO Funnel has its own
// TaskFilterBar, so the global agency/account chrome hides completely there
// (state persists for every other route; pure chrome removal).
const FILTERLESS_ROUTES = new Set(['tasks']);

// Routes that hide the main NavSidebar for full-page takeover (like ChatGPT).
// command-center left this set (P3 follow-up, 2026-08-12): its private
// desktop shell (own rail + top bar + 100vh takeover) was dead chrome on
// phones — pages flow inside the ONE mobile-first app shell.
const FULLSCREEN_ROUTES = new Set(['sales-ai']);

function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // State Management
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Navigation State
  const [activeTab, setActiveTab] = useState('home');
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Where "Back"/Esc from Settings returns to — the last non-settings
  // location (path + query), so exiting restores filters and detail pages.
  const lastNonSettingsPath = React.useRef('/home');
  // Sidebar: 'collapsed' (hidden), 'icons' (icon rail), 'expanded' (icons + labels)
  const [sidebarMode, setSidebarMode] = useState(() => {
    const stored = localStorage.getItem('sidebarMode');
    if (stored) return stored;
    // On phones every non-collapsed mode renders as a full open drawer —
    // an 'icons' default meant first visits started with the nav covering
    // the whole page. Phones start closed; the hamburger opens it.
    return window.innerWidth < 768 ? 'collapsed' : 'icons';
  });
  // Crossing the phone breakpoint changes what a mode MEANS: below 768px
  // every non-collapsed mode renders as a full open drawer, so a rail that
  // was "icons" on the desktop became a drawer over the page the moment the
  // window shrank (Ryan, 2026-09-14). Watch the breakpoint: shrinking closes
  // the drawer without touching the saved preference; growing restores it.
  const desktopModeRef = React.useRef(null);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = (isPhone) => {
      setSidebarMode((prev) => {
        if (isPhone) {
          if (prev === 'collapsed') return prev;
          desktopModeRef.current = prev;
          return 'collapsed';
        }
        const restore = desktopModeRef.current || localStorage.getItem('sidebarMode');
        desktopModeRef.current = null;
        return restore && restore !== 'collapsed' ? restore : prev;
      });
    };
    if (mq.matches) apply(true);
    const onChange = (e) => apply(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const { user, signOut } = useAuth();
  const {
    role, roleLoading, roleError, retryRole,
    isAdmin, isCustomer, isCiscoUser, isPaloUser, isSuperAdmin, isFeatureEnabled,
  } = useRole();

  /*
   * A bare spinner is fine for a moment and a lie for a minute.
   *
   * This gate blocks the WHOLE app on /api/auth/me, and apiClient's timeout is
   * 45s — so a slow backend bought 45 seconds of unexplained spinner with no
   * way out but a page reload. Seen for real on 2026-09-16 while the database
   * was under load. After 6s the wait admits it is a wait and offers a retry;
   * the spinner itself is unchanged for the common fast case.
   */
  const [slowGate, setSlowGate] = useState(false);
  useEffect(() => {
    if (!roleLoading) { setSlowGate(false); return undefined; }
    const t = setTimeout(() => setSlowGate(true), 6000);
    return () => clearTimeout(t);
  }, [roleLoading]);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const {
    pinnedDepartments,
    setPinnedDepartments,
    departmentOrder,
    setDepartmentOrder,
  } = usePinnedDepartments();

  // Account lists for filtering by saved account groups.
  // Capture the whole hook result so it can be shared via ListsContext to
  // descendant data pages (single source of truth — see contexts/ListsContext).
  const accountListsState = useAccountLists();
  const {
    lists: accountLists,
    selectedListId: selectedAccountListId,
    selectedList: selectedAccountList,
    selectedMembers: accountListMembers,
    selectList: selectAccountList,
    clearList: clearAccountList,
  } = accountListsState;

  // Global time filter for consistent filtering across all pages.
  // Capture the whole hook result so it can be shared via TimeFilterContext to
  // descendant data pages (single source of truth — see contexts/TimeFilterContext).
  const timeFilterState = useTimeFilter();
  // WHICH fiscal year the FY axes mean (State FY / PANW FY / …). Called once
  // here so a flip in any view flips them all; persisted to localStorage.
  const fiscalBasisState = useFiscalBasis();
  const { timePeriod, setTimePeriod } = timeFilterState;

  // Connector health alerts → bell notifications that deep-link to the
  // failing Connections row (admin-only; no-op for other roles).
  useConnectorHealthAlerts();

  // Dynamic page title
  const pageKey = location.pathname.slice(1).split('/')[0] || 'dashboard';
  useDocumentTitle(PAGE_TITLES[pageKey] || null);

  // Breadcrumb items based on current route + department
  const breadcrumbItems = React.useMemo(() => {
    const items = [{ label: 'Home', onClick: () => navigate('/home') }];
    const isStatewide = STATEWIDE_ROUTES.has(pageKey);
    // Skip the department crumb on state-wide pages — including it
    // would falsely suggest the page is filtered to that department.
    if (selectedDepartment && !isStatewide && pageKey !== 'atlas') {
      items.push({
        label: selectedDepartment.acronym || selectedDepartment.common_name || 'Department',
      });
    }
    if (isStatewide) {
      items.push({ label: 'California (state-wide)' });
    }

    // Handle Atlas sub-routes specially
    if (pageKey === 'atlas') {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      // pathSegments: ['atlas'] or ['atlas', 'personas'] or ['atlas', 'personas', 'ciso']

      // Always add Atlas as clickable
      items.push({ label: 'Atlas', onClick: () => navigate('/atlas') });

      // Get sub-route (e.g., 'personas', 'scenarios')
      const subRoute = pathSegments[1];
      if (subRoute && subRoute !== 'home' && ATLAS_ROUTES[subRoute]) {
        items.push({
          label: ATLAS_ROUTES[subRoute],
          onClick: () => navigate(`/atlas/${subRoute}`),
        });

        // Check for detail page (third segment like persona ID or scenario ID)
        const detailId = pathSegments[2];
        if (detailId) {
          // Try to get the dynamic label from localStorage (set by Atlas detail pages)
          const dynamicLabel = localStorage.getItem('atlas:breadcrumbLabel');
          const label = dynamicLabel || detailId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          items.push({ label });
        }
      }
    } else {
      // Skip the page crumb on /home and /dashboard — the trail already
      // starts at "Home", so adding it again reads "Home > FTB > Home".
      const pageName = PAGE_TITLES[pageKey];
      if (pageName && pageKey !== 'dashboard' && pageKey !== 'home') {
        items.push({ label: pageName });
      }
    }
    return items.length > 1 ? items : [];
  }, [pageKey, selectedDepartment, navigate, location.pathname]);

  const currentUser = user
    ? {
        name: user.user_metadata?.full_name || user.email,
        email: user.email,
        avatar: user.user_metadata?.avatar_url,
      }
    : null;

  // API Functions
  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      logger.api.info('Fetching departments');

      const data = await apiClient.get('/api/departments');
      logger.api.info('Fetched departments', { count: data.length });

      const sortedDepartments = data.sort((a, b) => {
        if (a.department_number && b.department_number) {
          return a.department_number.localeCompare(b.department_number);
        }
        if (a.department_number) return -1;
        if (b.department_number) return 1;
        return (a.official_name || '').localeCompare(b.official_name || '');
      });

      setDepartments(sortedDepartments);
      setError(null);

      // Restore previously selected department
      const params = new URLSearchParams(window.location.search);
      const urlDeptId = params.get('dept');
      const savedDeptId = urlDeptId || localStorage.getItem('selectedDepartmentId');
      if (savedDeptId && sortedDepartments.length > 0) {
        const savedDept = sortedDepartments.find(d => d.department_id === parseInt(savedDeptId));
        if (savedDept) {
          setSelectedDepartment(savedDept);
        }
      }
    } catch (err) {
      logger.api.error('Error fetching departments', null, err);
      setError(err.message);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      logger.api.info('Fetching customers');
      const data = await apiClient.get('/customers');
      setCustomers(data);
      logger.api.info('Fetched customers', { count: data.length });
    } catch (error) {
      logger.api.error('Error fetching customers', null, error);
      setCustomers([]);
    }
  }, []);

  // Sync activeTab with current route
  useEffect(() => {
    const path = location.pathname.slice(1) || 'tech-stack';
    setActiveTab(path);
    if (!location.pathname.startsWith('/settings')) {
      lastNonSettingsPath.current = `${location.pathname}${location.search}`;
    }
  }, [location]);

  // Initialize app when user is authenticated
  useEffect(() => {
    if (user) {
      logger.auth.info('MainApp initialized, user authenticated');
      fetchDepartments();
      fetchCustomers();
    }
  }, [user, fetchDepartments, fetchCustomers]);

  // Listen for session expiry (Supabase SIGNED_OUT event)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' && !showSessionExpired) {
        if (user) {
          setShowSessionExpired(true);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [user, showSessionExpired]);

  // Log selected department changes
  useEffect(() => {
    if (selectedDepartment) {
      logger.state.changed('MainApp', 'selectedDepartment', selectedDepartment.official_name);
    }
  }, [selectedDepartment]);

  // Handle back/forward button navigation
  useEffect(() => {
    const urlDeptId = searchParams.get('dept');
    if (urlDeptId && departments.length > 0) {
      const deptId = parseInt(urlDeptId);
      if (selectedDepartment?.department_id !== deptId) {
        const dept = departments.find(d => d.department_id === deptId);
        if (dept) {
          setSelectedDepartment(dept);
          localStorage.setItem('selectedDepartmentId', urlDeptId);
        }
      }
    }
  }, [searchParams, departments, selectedDepartment?.department_id]);

  // Handler Functions
  const handleDepartmentSelect = (departmentId) => {
    const department = departments.find(dept => dept.department_id === departmentId);
    if (department) {
      setSelectedDepartment(department);
      localStorage.setItem('selectedDepartmentId', departmentId.toString());
      setSearchParams(prev => {
        prev.set('dept', departmentId.toString());
        return prev;
      });
      logger.ui.clicked('department', { name: department.official_name, id: departmentId });
    }
  };

  const handleSelectAllDepartments = () => {
    setSelectedDepartment(null);
    localStorage.removeItem('selectedDepartmentId');
    setSearchParams(prev => {
      prev.delete('dept');
      return prev;
    });
    logger.ui.clicked('department', { name: 'All Departments', id: null });
  };

  const handleSettingsClick = () => {
    navigate(`/settings/${DEFAULT_SECTION}`);
  };

  // Deep-link helper — the contract every "fix it on Settings → Connections"
  // pointer depends on: `window.openSettings(section, { connector })` lands
  // on the routed section (connector rides along as ?connector= and the row
  // scrolls/expands/flashes). Legacy section ids and evicted workbenches
  // resolve through the alias maps.
  useEffect(() => {
    window.openSettings = (section, opts = {}) => {
      const workspace = LEGACY_SECTION_ROUTES[section];
      if (workspace) {
        navigate(workspace);
        return;
      }
      const target = SECTION_ALIASES[section] || section || DEFAULT_SECTION;
      const connector = opts.connector
        ? `?connector=${encodeURIComponent(opts.connector)}`
        : '';
      navigate(`/settings/${target}${connector}`);
    };
    return () => {
      if (window.openSettings) delete window.openSettings;
    };
  }, [navigate]);

  // Only a DESKTOP choice is a preference. On a phone the modes mean
  // "drawer open / closed", and persisting a drawer close as 'collapsed'
  // came back as a rail-less desktop the next time the window grew.
  const persistSidebarMode = (mode) => {
    if (window.innerWidth >= 768) localStorage.setItem('sidebarMode', mode);
  };

  const handleSidebarToggle = () => {
    setSidebarMode(prev => {
      let next;
      if (window.innerWidth < 768) {
        // Mobile: simple toggle — collapsed ↔ expanded (no icon rail)
        next = prev === 'collapsed' ? 'expanded' : 'collapsed';
      } else {
        const modes = ['collapsed', 'icons', 'expanded'];
        next = modes[(modes.indexOf(prev) + 1) % modes.length];
      }
      persistSidebarMode(next);
      return next;
    });
  };

  const handleSidebarModeChange = (mode) => {
    setSidebarMode(mode);
    persistSidebarMode(mode);
  };

  const handleTabChange = (tab) => {
    setIsTransitioning(true);
    navigate(`/${tab}`);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 150);
  };

  const handleExitSettings = useCallback(() => {
    navigate(lastNonSettingsPath.current || '/home');
  }, [navigate]);

  // Notification handlers
  const handleMarkNotificationRead = useCallback((id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const handleMarkAllNotificationsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const handleClearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Expose addNotification globally so services can push notifications
  useEffect(() => {
    window.addNotification = (notification) => {
      setNotifications(prev => [{
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        read: false,
        ...notification,
      }, ...prev].slice(0, 50));
    };
    return () => { delete window.addNotification; };
  }, []);

  const handleTogglePin = useCallback((deptId) => {
    const strId = String(deptId);
    const currentPins = pinnedDepartments.map(String);
    if (currentPins.includes(strId)) {
      // Unpin
      setPinnedDepartments(currentPins.filter(id => id !== strId));
      setDepartmentOrder(prev => prev.filter(id => String(id) !== strId));
    } else {
      // Pin
      const newPins = [...currentPins, strId];
      setPinnedDepartments(newPins);
      setDepartmentOrder(prev => [...prev, strId]);
    }
  }, [pinnedDepartments, setPinnedDepartments, setDepartmentOrder]);

  const handleReorderPins = useCallback((newOrder) => {
    setDepartmentOrder(newOrder);
  }, [setDepartmentOrder]);

  const getDepartmentDisplayName = (department) => {
    if (!department) return null;

    if (department.acronym && department.common_name) {
      return `${department.acronym} - ${department.common_name}`;
    }

    return department.common_name || department.official_name || 'Unknown';
  };

  /*
   * Permissions could not be read. The hook fails CLOSED, so carrying on here
   * renders a stripped app — no admin, no features — with nothing saying why,
   * which reads as "your account lost its access" rather than "we could not
   * ask". useRole's own docstring asks callers to tell those apart via
   * roleError; until now nobody did.
   */
  if (roleError) {
    return (
      <div className="app-loading">
        <p>Could not check your permissions.</p>
        <p className="app-loading-sub">
          You are signed in — this is the entitlement lookup failing, not your account.
          Nothing has changed about what you can access.
        </p>
        <button type="button" className="app-loading-retry" onClick={() => retryRole()}>
          Try again
        </button>
      </div>
    );
  }

  // Loading State
  if (loading || roleLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>{roleLoading ? 'Checking permissions...' : 'Loading departments...'}</p>
        {slowGate && roleLoading && (
          <>
            <p className="app-loading-sub">
              This is taking longer than usual. The server may be busy.
            </p>
            <button type="button" className="app-loading-retry" onClick={() => retryRole()}>
              Try again
            </button>
          </>
        )}
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="app-error">
        <h2>Can’t reach TekStak</h2>
        <p className="app-error-copy">
          The server didn’t respond — it may be restarting after a deploy,
          or your connection dropped. This usually clears in a few seconds.
        </p>
        <button onClick={fetchDepartments} className="app-error-retry">
          Try again
        </button>
        {error && <p className="app-error-detail">{error}</p>}
      </div>
    );
  }

  // Main Application View
  return (
    <ClassificationAgentProvider>
    <TimeFilterProvider value={timeFilterState}>
    <FiscalBasisProvider value={fiscalBasisState}>
    <ListsProvider value={accountListsState}>
    <div className="app-container">
      <BackendUnreachableBanner />
      <ScraperAuthBanner />
      <TopNav
        selectedDepartment={selectedDepartment}
        departments={departments}
        onDepartmentSelect={handleDepartmentSelect}
        onSelectAllDepartments={handleSelectAllDepartments}
        currentUser={currentUser}
        onSettingsClick={handleSettingsClick}
        onLogout={signOut}
        onThemeToggle={toggleTheme}
        theme={theme}
        notifications={notifications}
        onMarkNotificationRead={handleMarkNotificationRead}
        onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
        onClearNotifications={handleClearNotifications}
        onToggleSidebar={handleSidebarToggle}
        sidebarMode={sidebarMode}
        pinnedDepartments={pinnedDepartments}
        departmentOrder={departmentOrder}
        onTogglePin={handleTogglePin}
        onReorderPins={handleReorderPins}
        hideDepartmentFilter={STATEWIDE_ROUTES.has(pageKey) || ACCOUNT_LIST_ROUTES.has(pageKey)}
        filterHidden={FILTERLESS_ROUTES.has(pageKey)}
        statewideLabel={STATEWIDE_ROUTES.has(pageKey) ? 'California (state-wide)' : null}
        accountListMode={ACCOUNT_LIST_ROUTES.has(pageKey)}
        accountLists={accountLists}
        selectedAccountListId={selectedAccountListId}
        selectedAccountList={selectedAccountList}
        accountListMembers={accountListMembers}
        onAccountListSelect={selectAccountList}
        onAccountListClear={clearAccountList}
        hideHamburger={FULLSCREEN_ROUTES.has(pageKey)}
      />

      <div className={`app-body ${FULLSCREEN_ROUTES.has(pageKey) ? 'app-body-fullscreen' : ''}`}>
        {/* Hide NavSidebar on fullscreen routes like Sales AI */}
        {!FULLSCREEN_ROUTES.has(pageKey) && (
          <NavSidebar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            sidebarMode={sidebarMode}
            onToggleMode={handleSidebarModeChange}
            role={role}
            isFeatureEnabled={isFeatureEnabled}
            isCustomer={isCustomer}
            isSuperAdmin={isSuperAdmin}
          />
        )}

        <div className="main-wrapper">
          {/* Hide breadcrumbs on fullscreen routes — and on the account page
            * once an agency is picked: the trail there read "Home › DGS ›
            * Accounts" under a filter bar that already says DGS and a rail
            * that already lights Accounts. A band that repeats two things
            * the eye just passed is 36px of nothing (Ryan, 2026-09-13). */}
          {breadcrumbItems.length > 0 && !FULLSCREEN_ROUTES.has(pageKey)
            && !(pageKey === 'accounts' && selectedDepartment) && (
            <Breadcrumbs items={breadcrumbItems} />
          )}

          <main className="main-content">
          <div className={`content-wrapper ${isTransitioning ? 'transitioning' : ''}`}>
            <Routes>
              {isCustomer ? (
                <>
                  <Route path="/zero-trust" element={
                    <ZeroTrust
                      selectedDepartment={selectedDepartment}
                      departments={departments}
                      customerMode={true}
                    />
                  } />
                  {/* Customers keep the personal settings sections
                      (profile/delete-account is an App Review requirement). */}
                  <Route path="/settings" element={<Navigate to={`/settings/${DEFAULT_SECTION}`} replace />} />
                  <Route path="/settings/:section" element={
                    <Settings
                      role={role}
                      isSuperAdmin={false}
                      onExitSettings={handleExitSettings}
                    />
                  } />
                  <Route path="*" element={<Navigate to="/zero-trust" replace />} />
                </>
              ) : (
                <>
                  <Route path="/" element={<Navigate to={isPaloUser ? "/welcome" : "/home"} replace />} />

                  {/* Palo Alto user welcome/onboarding page */}
                  <Route path="/welcome" element={<PaloWelcome />} />

                  <Route path="/home" element={
                    <Home
                      departments={departments}
                      onDepartmentSelect={handleDepartmentSelect}
                      onNavigate={handleTabChange}
                    />
                  } />

                  <Route path="/dashboard" element={
                    <Dashboard
                      selectedDepartmentId={selectedDepartment?.department_id}
                      selectedDepartmentName={getDepartmentDisplayName(selectedDepartment)}
                      selectedDepartment={selectedDepartment}
                      supabase={supabase}
                    />
                  } />

                  <Route path="/genai" element={
                    <GenAIDashboard
                      supabase={supabase}
                      selectedDepartment={selectedDepartment?.department_code || selectedDepartment?.department_id}
                      departmentName={getDepartmentDisplayName(selectedDepartment)}
                    />
                  } />

                  <Route path="/sankey" element={
                    <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
                      <SankeyFlowChart
                        supabase={supabase}
                        selectedDepartment={selectedDepartment}
                      />
                    </React.Suspense>
                  } />

                  {/* Brand assets moved to Settings → Admin → Brand assets
                      (2026-08 redesign); the orphan /image-manager and
                      /images URLs redirect there. */}
                  <Route path="/image-manager" element={<Navigate to="/settings/brand-assets" replace />} />
                  <Route path="/images" element={<Navigate to="/settings/brand-assets" replace />} />
                  <Route path="/account-lists" element={<AccountListsPage />} />
                  <Route path="/quotes" element={<QuotesPage />} />
                  <Route path="/connection-types" element={<ConnectionTypes />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/capabilities" element={<Capabilities />} />

                  {/* Solicitations / RFQ virtual document view (H.18.1 Phase 7.4.D).
                      Index list at /solicitations, detail at /solicitations/:id. */}
                  <Route path="/solicitations" element={<SolicitationList />} />
                  <Route path="/solicitations/:id" element={<SolicitationDetail />} />

                  <Route path="/review" element={<Navigate to="/classification/overview" replace />} />

                  {/* Classification — six sub-pages under one parent.
                      Each is its own page (no shared layout) so deep
                      links work and the sidebar drives navigation. */}
                  <Route path="/classification" element={<Navigate to="/classification/overview" replace />} />

                  <Route path="/classification/overview" element={
                    <React.Suspense fallback={<div className="ts-loading-fallback">Loading Classification…</div>}>
                      <ClassificationUnified
                        department={selectedDepartment?.official_name || selectedDepartment?.common_name}
                        departmentId={selectedDepartment?.department_id}
                      />
                    </React.Suspense>
                  } />

                  <Route path="/classification/health" element={<ClassifierHealth />} />

                  <Route path="/classification/triage" element={<Triage />} />

                  <Route path="/classification/flags" element={
                    <FlagReview
                      department={selectedDepartment?.official_name || selectedDepartment?.common_name}
                      departmentId={selectedDepartment?.department_id}
                    />
                  } />

                  <Route path="/classification/rules" element={<RuleCandidates />} />

                  {/* Backlog Buster — admin + cisco_user only. Customers
                      never reach this branch (handled above). */}
                  {isCiscoUser && (
                    <Route path="/backlog-buster" element={<BacklogBuster />} />
                  )}

                  <Route path="/zero-trust" element={
                    <ZeroTrust
                      selectedDepartment={selectedDepartment}
                      departments={departments}
                    />
                  } />

                  <Route path="/tech-stack" element={
                    <TechStackAnalysis
                      selectedDepartment={selectedDepartment}
                    />
                  } />

                  <Route path="/data" element={
                    <DataGapAnalysis
                      selectedDepartment={selectedDepartment}
                    />
                  } />

                  {isFeatureEnabled('scraper_ops') && (
                    <Route path="/scraper" element={
                      <ScraperDashboard selectedDepartment={selectedDepartment} />
                    } />
                  )}

                  {isFeatureEnabled('vendor_analysis') && (
                    <Route path="/vendor-analysis" element={<VendorCompetitiveAnalysis />} />
                  )}

                  {/* GAS-parity Vendors page (P6) — same panels re-hosted
                      in the GAS nine-tab anatomy; same gate as
                      /vendor-analysis. */}
                  {isFeatureEnabled('vendor_analysis') && (
                    <Route path="/vendors" element={<VendorsPage />} />
                  )}

                  {/* Stack Map — game-style tech-stack simulation
                      (codename TechWorld in source / docs).
                      Phase 1: R3F scene rendering live tech-stack data
                      for the selectedDepartment. Open to all non-customer
                      roles during dogfood (customer block is the upper
                      branch of this ternary).
                      See TECHWORLD_NORTH_STAR.md / TECHWORLD_DESIGN.md. */}
                  <Route path="/stack-map" element={
                    <ThreeDRoute>
                      <TechWorld selectedDepartment={selectedDepartment} />
                    </ThreeDRoute>
                  } />

                  {/* Stack Map Lab — admin-only prototype workshop.
                      Per TECHWORLD_NORTH_STAR.md M4 + D7. Hidden from
                      nav by design — reached by URL only.
                      Passes selectedDepartment so prototypes can scope
                      their data to the rep's current department choice.
                      See src/components/TechWorld/Lab/README.md. */}
                  <Route path="/stack-map/lab" element={
                    <ThreeDRoute>
                      <TechWorldLab selectedDepartment={selectedDepartment} />
                    </ThreeDRoute>
                  } />

                  {/* Atlas — Cybersecurity portfolio education module.
                      Restricted to admin and palo_user roles only.
                      Gated by 'atlas' feature flag. See ATLAS_BUILD_PLAN.md
                      and src/components/Atlas/CLAUDE.md for module docs. */}
                  {(isAdmin || isPaloUser) && isFeatureEnabled('atlas') && (
                    <Route path="/atlas/*" element={
                      <ThreeDRoute><AtlasRoot /></ThreeDRoute>
                    } />
                  )}

                  {/* Sales AI Canvas — full-page AI workspace with split
                      chat + widget canvas layout. Admin only during development. */}
                  {isAdmin && (
                    <>
                      <Route path="/sales-ai" element={
                        <SalesAICanvas
                          selectedDepartment={selectedDepartment}
                          selectedAccountList={selectedAccountList}
                          accountListMembers={accountListMembers}
                        />
                      } />
                      <Route path="/workspace" element={<AccountWorkspace selectedDepartment={selectedDepartment} />} />
                      <Route path="/accounts" element={
                        <AccountsPage
                          selectedDepartment={selectedDepartment}
                          departments={departments}
                          onDepartmentSelect={handleDepartmentSelect}
                        />
                      } />
                      <Route path="/digest" element={<DailyDigest />} />
                      {isFeatureEnabled('news_feed') && (
                        <Route path="/news" element={<NewsFeed departments={departments} selectedDepartment={selectedDepartment} onDepartmentSelect={handleDepartmentSelect} />} />
                      )}
                      <Route path="/recordings" element={<RecordingsPage />} />
                      <Route path="/tasks" element={<TasksPage />} />
                      <Route path="/command-center" element={<CommandCenterPage />} />
                      <Route path="/contacts" element={
                        <ContactsPage
                          departments={departments}
                          onDepartmentSelect={handleDepartmentSelect}
                        />
                      } />
                      <Route path="/opportunities" element={<OpportunitiesPage />} />
                      <Route path="/opportunities/:opportunityId" element={<OpportunityDetail />} />
                    </>
                  )}

                  {/* Memory Dashboard — observability for Sales AI memory system.
                      Admin only. Can be toggled off per-user via Settings.
                      See Phase 6 in SALES_AI_ROADMAP.md. */}
                  {isAdmin && (
                    <Route path="/memory" element={<MemoryDashboard />} />
                  )}

                  {/* Settings — routed takeover; every section is a URL.
                      /settings lands on the default (personal) section. */}
                  <Route path="/settings" element={<Navigate to={`/settings/${DEFAULT_SECTION}`} replace />} />
                  <Route path="/settings/:section" element={
                    <Settings
                      role={role}
                      isSuperAdmin={isSuperAdmin}
                      onExitSettings={handleExitSettings}
                    />
                  } />

                  {/* Data workbenches — evicted from Settings (2026-08).
                      Same gates they had as settings sections. */}
                  {isFeatureEnabled('classification_hub') && (
                    <Route path="/classification-hub" element={
                      <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
                        <div className="workbench-page">
                          <MainClassificationPage
                            customerId={selectedCustomer?.customer_id || 1}
                            customerName={selectedCustomer?.customer_name || 'Unknown'}
                          />
                        </div>
                      </React.Suspense>
                    } />
                  )}
                  {isFeatureEnabled('procurement_dashboard') && (
                    <Route path="/procurement" element={
                      <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
                        <div className="workbench-page">
                          <IntegratedProcurementDashboard
                            customerId={selectedCustomer?.customer_id || 1}
                            customerName={selectedCustomer?.customer_name || 'Unknown'}
                          />
                        </div>
                      </React.Suspense>
                    } />
                  )}
                  {isFeatureEnabled('department_management') && (
                    <Route path="/departments" element={
                      <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
                        <div className="workbench-page"><DepartmentManager /></div>
                      </React.Suspense>
                    } />
                  )}
                  {/* Department Branding Library — public agency marks, open to every
                      signed-in role (writes are ownership-gated server-side). */}
                  <Route path="/brand-library" element={
                    <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
                      <DepartmentBrandLibrary />
                    </React.Suspense>
                  } />
                  {isAdmin && (
                    <>
                      <Route path="/zt-mapping" element={
                        <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
                          <div className="workbench-page"><ZTCategoryMapping /></div>
                        </React.Suspense>
                      } />
                      <Route path="/zt-mapping-eval" element={
                        <React.Suspense fallback={<div className="ts-loading-fallback">Loading…</div>}>
                          <div className="workbench-page"><ZTMappingEval /></div>
                        </React.Suspense>
                      } />
                    </>
                  )}

                  {/* 404 catch-all */}
                  <Route path="*" element={<NotFound />} />
                </>
              )}
            </Routes>
          </div>
        </main>
        </div>

      </div>

      {/* Mobile shell (native app / narrow viewport): bottom tab bar for
          the phone-first views. Renders null on desktop and for customers;
          NavSidebar's drawer still reaches every other route. */}
      <MobileTabBar
        isAdmin={isAdmin}
        isCustomer={isCustomer}
        isFeatureEnabled={isFeatureEnabled}
        hidden={FULLSCREEN_ROUTES.has(pageKey)}
      />

      {/* Session Expired Toast */}
      {showSessionExpired && (
        <SessionExpired
          onDismiss={() => setShowSessionExpired(false)}
          onLogin={() => {
            setShowSessionExpired(false);
            navigate('/login');
          }}
        />
      )}

      {/* AI Chat Widget (access controlled per-user on backend) */}
      {/* Hidden on /sales-ai (its own chat) and /settings (full takeover) */}
      {!location.pathname.startsWith('/sales-ai') && !location.pathname.startsWith('/settings') && (
        <ChatWidget selectedDepartment={selectedDepartment} />
      )}

      {/* Global quick-capture — `q` / Cmd+Shift+K / floating button → GTD inbox */}
      {!location.pathname.startsWith('/settings') && <QuickCapture />}


      {/* Classification Agent Panel (admin only) */}
      {isAdmin && <ClassificationAgentPanel />}
    </div>
    </ListsProvider>
    </FiscalBasisProvider>
    </TimeFilterProvider>
    </ClassificationAgentProvider>
  );
}

export default MainApp;
